import Decimal from 'decimal.js';
import { buildMetricValueCsv } from './csvExport';
import { convertUsdToEur } from './eurFx';
import { nativeCroEurPerUnit, wrappedBtcEurPerUnit } from './cryptoPrices';
import { applyFiBasisToFifoResult } from './fiDisposalBasis';
import { runFifo } from './fifo';
import type {
  DisposalDetail,
  FifoResult,
  LedgerClassification,
  LedgerEvent,
  LotBasisRepairMeta,
  OwnedWallet,
} from './types';
import { applyCrossEnvironmentContinuity } from './crossEnvironmentContinuity';
import type { CrossEnvironmentContinuityStats } from './crossEnvironmentContinuity';
import {
  applyCronosBridgeMigrationContinuity,
  type CronosBridgeContinuityStats,
} from './cronosBridgeContinuity';
import { mergedCronosBridgeContracts } from './cronosBridgeContracts';
import { injectSyntheticStablecoinAcquisitionsForShortfall } from './missingStablecoinLotBackfill';
import {
  applyWalletContinuityLedgerPrep,
  fifoResultWithoutWalletContinuity,
  ownedAddressSetFromWallets,
  runFifoWithOwnedWalletContinuity,
} from './walletLotContinuity';
import type { WalletContinuityFifoStats } from './walletLotContinuity';

const ZERO_EPS = new Decimal('1e-18');

const STABLE_USD = new Set(['USDC', 'USDT', 'DAI', 'BUSD', 'TUSD']);

function parseMetaNumber(raw: string | undefined): Decimal | null {
  if (raw == null) return null;
  const t = String(raw).trim().replace(/\s/g, '');
  if (!t) return null;
  let x = t;
  if (x.includes(',') && x.includes('.')) x = x.replace(/,/g, '');
  else if (x.includes(',') && !x.includes('.')) x = x.replace(',', '.');
  try {
    const n = new Decimal(x);
    return n.isFinite() ? n : null;
  } catch {
    return null;
  }
}

function cloneLedgerEvents(events: LedgerEvent[]): LedgerEvent[] {
  return events.map((e) => ({
    ...e,
    amount: new Decimal(e.amount),
    eurValue: e.eurValue != null ? new Decimal(e.eurValue) : null,
    csvMeta: e.csvMeta ? { ...e.csvMeta } : undefined,
    acquisitionProvenance: e.acquisitionProvenance,
    acquisitionProvenanceFlag: e.acquisitionProvenanceFlag,
  }));
}

function isExcludedClassification(c?: LedgerClassification): boolean {
  return (
    c === 'INTERNAL_TRANSFER' ||
    c === 'SELF_TRANSFER' ||
    c === 'APPROVAL' ||
    c === 'IGNORE_NON_TAX'
  );
}

/** Eligible for repair attempt (not same as successfully repaired). */
export function isLotBasisRepairCandidate(ev: LedgerEvent): boolean {
  if (ev.kind !== 'acquisition' && ev.kind !== 'income') return false;
  if (isExcludedClassification(ev.classification)) return false;
  if (!ev.amount || !ev.amount.gt(0)) return false;
  if (!ev.ts || ev.ts <= 0) return false;
  if (ev.classification === 'UNKNOWN_REVIEW') {
    if (
      ev.fiTaxCategory !== 'TAXABLE_INCOME' &&
      ev.fiTaxCategory !== 'ACQUISITION'
    ) {
      return false;
    }
  }
  const v = ev.eurValue;
  if (v == null) return true;
  return !v.gt(ZERO_EPS);
}

function repairMeta(
  method: string,
  confidence: LotBasisRepairMeta['basisRepairConfidence'],
  ref: string | undefined,
  opts?: {
    basisValuationSource?: string;
    basisValuationFlag?: 'fallback_priced' | 'normal';
  }
): LotBasisRepairMeta {
  return {
    basisRepaired: true,
    basisRepairMethod: method,
    basisRepairConfidence: confidence,
    basisRepairSourceRef: ref,
    basisRepairPass: 2,
    basisValuationSource: opts?.basisValuationSource ?? method,
    basisValuationFlag: opts?.basisValuationFlag ?? 'fallback_priced',
  };
}

/** Before repair: known basis from import vs unknown / needs review. */
function initAcquisitionProvenance(events: LedgerEvent[]): void {
  for (const ev of events) {
    if (ev.kind !== 'acquisition' && ev.kind !== 'income') continue;
    if (isExcludedClassification(ev.classification)) continue;
    if (ev.eurValue != null && ev.eurValue.gt(ZERO_EPS)) {
      ev.acquisitionProvenance = 'known';
      ev.acquisitionProvenanceFlag = 'normal';
    } else {
      ev.acquisitionProvenance = 'unknown';
      ev.acquisitionProvenanceFlag = 'missing_provenance_review';
    }
  }
}

/**
 * Priority 1 (sync): native EUR in Crypto.com row meta.
 */
function tryRepairExplicitNativeEur(ev: LedgerEvent): boolean {
  const m = ev.csvMeta;
  if (!m) return false;
  const nc = m.nativeCurrency?.trim().toUpperCase().replace(/\s/g, '');
  const na = parseMetaNumber(m.nativeAmount);
  if (!na || !na.gt(0)) return false;
  if (nc === 'EUR' || nc === 'EURO') {
    ev.eurValue = na.abs();
    ev.lotBasisRepair = repairMeta('explicit_native_eur_csv', 'high', ev.ref, {
      basisValuationSource: 'explicit_native_eur_csv',
      basisValuationFlag: 'normal',
    });
    ev.acquisitionProvenance = 'recovered';
    ev.acquisitionProvenanceFlag = 'normal';
    return true;
  }
  return false;
}

/**
 * Same ref + same asset: weighted average EUR/unit from all priced sibling legs (no row merging).
 */
function repairSiblingSameRefWeightedSameAsset(events: LedgerEvent[]): number {
  const byRef = new Map<string, LedgerEvent[]>();
  for (const ev of events) {
    if (!ev.ref) continue;
    if (ev.kind !== 'acquisition' && ev.kind !== 'income') continue;
    if (isExcludedClassification(ev.classification)) continue;
    const k = ev.ref.toLowerCase();
    const arr = byRef.get(k) ?? [];
    arr.push(ev);
    byRef.set(k, arr);
  }

  let n = 0;
  for (const [, legs] of Array.from(byRef.entries())) {
    const byAsset = new Map<string, LedgerEvent[]>();
    for (const e of legs) {
      const a = e.asset.toUpperCase();
      const arr = byAsset.get(a) ?? [];
      arr.push(e);
      byAsset.set(a, arr);
    }
    for (const [, assetLegs] of Array.from(byAsset.entries())) {
      const priced = assetLegs.filter(
        (e) => e.eurValue != null && e.eurValue.gt(ZERO_EPS)
      );
      const need = assetLegs.filter((e) => isLotBasisRepairCandidate(e));
      if (priced.length === 0 || need.length === 0) continue;
      const sumEur = priced.reduce((s, e) => s.plus(e.eurValue!), new Decimal(0));
      const sumAmt = priced.reduce((s, e) => s.plus(e.amount), new Decimal(0));
      if (!sumAmt.gt(0)) continue;
      const unit = sumEur.div(sumAmt);
      if (!unit.gt(0)) continue;
      for (const u of need) {
        const eur = unit.mul(u.amount);
        if (!eur.gt(ZERO_EPS)) continue;
        u.eurValue = eur;
        u.lotBasisRepair = repairMeta(
          'grouped_sibling_weighted_avg',
          'medium',
          u.ref,
          {
            basisValuationSource: 'grouped_sibling_weighted_avg',
            basisValuationFlag: 'normal',
          }
        );
        mergeFlags(u, ['lot_basis_repair_sibling_weighted']);
        u.acquisitionProvenance = 'recovered';
        u.acquisitionProvenanceFlag = 'normal';
        n++;
      }
    }
  }
  return n;
}

function mergeFlags(ev: LedgerEvent, extra: string[]): void {
  ev.reviewFlags = Array.from(new Set([...(ev.reviewFlags ?? []), ...extra]));
}

/** CAW token on Cronos — presale deposits are native CRO to this contract; claims are a separate tx (`Claim`). */
const CAW_TOKEN_CONTRACT = '0xccccccccdbec186dc426f8b5628af94737df0e60'.toLowerCase();

function isCawPresaleNativePayment(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos' || ev.kind !== 'disposal') return false;
  if (ev.asset.toUpperCase() !== 'CRO') return false;
  return (ev.toAddress ?? '').toLowerCase() === CAW_TOKEN_CONTRACT;
}

function isCawClaimTokenAcquisition(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos' || ev.kind !== 'acquisition') return false;
  if (ev.asset.toUpperCase() !== 'CAW') return false;
  return (ev.tokenContractAddress ?? '').toLowerCase() === CAW_TOKEN_CONTRACT;
}

function walletKeyForPresale(ev: LedgerEvent): string {
  return (ev.primaryWalletAddress ?? ev.fromAddress ?? '').toLowerCase();
}

function walletKeyForClaim(ev: LedgerEvent): string {
  return (ev.primaryWalletAddress ?? ev.toAddress ?? '').toLowerCase();
}

/**
 * CAW presale: user sends native CRO to the token contract in one tx; later `Claim` mints/transfers CAW in another tx.
 * Acquisition EUR = EUR value of the CRO paid (same economic basis as the presale disposal proceeds).
 */
async function repairCawPresaleClaimAcquisitionBasis(
  events: LedgerEvent[],
  signal?: AbortSignal
): Promise<number> {
  const presales = events
    .filter(isCawPresaleNativePayment)
    .map((ev) => ({ ev, w: walletKeyForPresale(ev), ts: ev.ts }))
    .filter((x) => x.w)
    .sort((a, b) => a.ts - b.ts);

  const claims = events
    .filter(isCawClaimTokenAcquisition)
    .map((ev) => ({ ev, w: walletKeyForClaim(ev), ts: ev.ts }))
    .filter((x) => x.w)
    .sort((a, b) => a.ts - b.ts);

  if (presales.length === 0 || claims.length === 0) return 0;

  const used = new Set<LedgerEvent>();
  let n = 0;

  for (const c of claims) {
    if (signal?.aborted) break;
    if (!isLotBasisRepairCandidate(c.ev)) continue;

    const p = presales.find(
      (x) =>
        x.w === c.w &&
        x.ts < c.ts &&
        !used.has(x.ev)
    );
    if (!p) continue;

    let basis = p.ev.eurValue;
    if (basis == null || !basis.gt(ZERO_EPS)) {
      try {
        const unit = await nativeCroEurPerUnit(
          new Date(p.ev.ts),
          p.ev.cronosPriceHint?.blockNumber
        );
        if (unit != null && unit.gt(0)) {
          basis = unit.mul(p.ev.amount.abs());
        }
      } catch {
        basis = null;
      }
    }
    if (basis == null || !basis.gt(ZERO_EPS)) continue;

    used.add(p.ev);
    c.ev.eurValue = basis;
    c.ev.lotBasisRepair = repairMeta('caw_presale_cro_basis', 'high', c.ev.ref, {
      basisValuationSource: 'caw_presale_cro_payment',
      basisValuationFlag: 'normal',
    });
    mergeFlags(c.ev, ['caw_presale_claim_basis']);
    c.ev.acquisitionProvenance = 'recovered';
    c.ev.acquisitionProvenanceFlag = 'normal';
    n++;
  }

  return n;
}

/**
 * Async: native USD in csvMeta, USDC parity, WBTC≈BTC historic, CRO historic.
 */
async function repairAsyncFallbacks(
  events: LedgerEvent[],
  signal?: AbortSignal
): Promise<number> {
  let n = await repairCawPresaleClaimAcquisitionBasis(events, signal);

  for (const ev of events) {
    if (signal?.aborted) break;
    if (!isLotBasisRepairCandidate(ev)) continue;

    if (tryRepairExplicitNativeEur(ev)) {
      n++;
      continue;
    }

    const m = ev.csvMeta;
    if (m) {
      const nc = m.nativeCurrency?.trim().toUpperCase().replace(/\s/g, '');
      const na = parseMetaNumber(m.nativeAmount);
      if (na?.gt(0) && nc === 'USD') {
        try {
          const eur = await convertUsdToEur(na.abs(), new Date(ev.ts));
          if (eur.gt(ZERO_EPS)) {
            ev.eurValue = eur;
            ev.lotBasisRepair = repairMeta('explicit_native_usd_fx', 'high', ev.ref, {
              basisValuationSource: 'explicit_native_usd_fx',
              basisValuationFlag: 'normal',
            });
            ev.acquisitionProvenance = 'recovered';
            ev.acquisitionProvenanceFlag = 'normal';
            n++;
            continue;
          }
        } catch {
          /* fall through */
        }
      }
    }

    const sym = ev.asset.toUpperCase();
    if (STABLE_USD.has(sym)) {
      try {
        const eur = await convertUsdToEur(ev.amount.abs(), new Date(ev.ts));
        if (eur.gt(ZERO_EPS)) {
          ev.eurValue = eur;
          ev.lotBasisRepair = repairMeta('usdc_usd_parity_fx', 'medium', ev.ref, {
            basisValuationSource: 'usdc_usd_parity_fx',
            basisValuationFlag: 'fallback_priced',
          });
          mergeFlags(ev, ['lot_basis_repair_usdc_fx']);
          ev.acquisitionProvenance = 'recovered';
          ev.acquisitionProvenanceFlag = 'normal';
          n++;
          continue;
        }
      } catch {
        /* fall through */
      }
    }

    if (sym === 'WBTC') {
      try {
        const unit = await wrappedBtcEurPerUnit(new Date(ev.ts), null);
        if (unit != null && unit.gt(0)) {
          ev.eurValue = unit.mul(ev.amount.abs());
          ev.lotBasisRepair = repairMeta('wbtc_from_btc_historic', 'medium', ev.ref, {
            basisValuationSource: 'wbtc_from_btc_historic',
            basisValuationFlag: 'fallback_priced',
          });
          mergeFlags(ev, ['lot_basis_repair_wbtc_btc_series']);
          ev.acquisitionProvenance = 'recovered';
          ev.acquisitionProvenanceFlag = 'normal';
          n++;
          continue;
        }
      } catch {
        /* unresolved */
      }
    }

    if (sym === 'CRO' || sym === 'WCRO') {
      try {
        const unit = await nativeCroEurPerUnit(
          new Date(ev.ts),
          ev.cronosPriceHint?.blockNumber
        );
        if (unit != null && unit.gt(0)) {
          ev.eurValue = unit.mul(ev.amount.abs());
          ev.lotBasisRepair = repairMeta('historical_cro_rate', 'medium', ev.ref, {
            basisValuationSource: 'cro_historic_rate',
            basisValuationFlag: 'fallback_priced',
          });
          mergeFlags(ev, ['lot_basis_repair_cro_historical']);
          ev.cronosPriceHint = undefined;
          ev.acquisitionProvenance = 'recovered';
          ev.acquisitionProvenanceFlag = 'normal';
          n++;
        }
      } catch {
        /* unresolved */
      }
    }
  }
  return n;
}

export type LotBasisRepairStats = {
  zeroBasisCandidatesPass1: number;
  repairedTotal: number;
  unresolvedCandidates: number;
  repairedByAsset: Record<string, number>;
  repairedByMethod: Record<string, number>;
  unresolvedByAsset: Record<string, number>;
  unresolvedByClassification: Record<string, number>;
  disposalsWithFifoCostZeroPass1: number;
  disposalsWithFifoCostZeroPass2: number;
  deltaFifoGainEur: Decimal;
  deltaChosenGainEur: Decimal;
  deemedOnlyBecauseFifoZeroPass1Estimate: number;
  /** Disposals where `valuationFlag === 'fallback_outgoing_used'` on input ledger. */
  disposalOutgoingFallbackCount: number;
  /** Synthetic stable acquisitions inserted when disposal exceeded tracked inventory (global FIFO only). */
  syntheticStableInventoryBackfills: number;
  /** Rows tagged as mission rewards (income tagging). */
  missionRewardRowCount: number;
  /** Details where pass1 chose deemed basis and pass2 chose FIFO (same disposal key). */
  deemedToFifoSwitchCount: number;
  /** Set when `ownedWallets` is provided to the pipeline. */
  walletContinuity?: WalletContinuityFifoStats;
  /** sum(chosen_gain wallet FIFO) − sum(chosen_gain global FIFO) on pass-2 repaired ledger. */
  continuityChosenGainDeltaVsGlobalEur?: Decimal;
  /** Pass-2 disposals whose matched lot still has `acquisitionProvenance === 'unknown'`. */
  disposalsUnknownAcquisitionProvenancePass2?: number;
  /** CDC ↔ on-chain self-owned reconciliation (before same-tx wallet prep). */
  crossEnvironment?: CrossEnvironmentContinuityStats;
  /** Cronos POS ↔ EVM bridge legs paired as non-taxable continuity (before wallet prep). */
  cronosBridge?: CronosBridgeContinuityStats;
  /**
   * sum(chosen_gain pass1 with bridge) − sum(chosen_gain pass1 without bridge), same prep (wallet + stablecoin).
   * Negative when bridge continuity removes phantom disposal gains.
   */
  bridgeContinuityChosenGainDeltaPass1Eur?: Decimal;
};

function detailMatchKey(d: DisposalDetail): string {
  return `${d.ref ?? ''}|${d.ts}|${d.asset}|${d.disposed.toFixed(12)}`;
}

function countDeemedToFifoSwitches(
  pass1Details: DisposalDetail[],
  pass2Details: DisposalDetail[]
): number {
  const p1 = new Map<string, DisposalDetail>();
  for (const d of pass1Details) p1.set(detailMatchKey(d), d);
  let n = 0;
  for (const d2 of pass2Details) {
    const d1 = p1.get(detailMatchKey(d2));
    if (!d1) continue;
    if (d1.basisMethodUsed === 'deemed' && d2.basisMethodUsed === 'fifo') n++;
  }
  return n;
}

function countOutgoingFallbackDisposals(events: LedgerEvent[]): number {
  return events.filter(
    (e) =>
      e.kind === 'disposal' && e.valuationFlag === 'fallback_outgoing_used'
  ).length;
}

function countMissionRewardRows(events: LedgerEvent[]): number {
  return events.filter((e) => e.fiSubreason === 'CDC_MISSION_REWARD').length;
}

function countFifoZeroCostDisposals(result: FifoResult): number {
  return result.details.filter((d) => d.costEur.lte(ZERO_EPS)).length;
}

function sumFifoGains(result: FifoResult): Decimal {
  return result.details.reduce((s, d) => s.plus(d.gainEur), new Decimal(0));
}

function sumChosenGains(result: FifoResult): Decimal {
  return result.details.reduce(
    (s, d) => s.plus(d.chosenGainEur ?? d.gainEur),
    new Decimal(0)
  );
}

function countDeemedWhileFifoLoss(
  details: ReturnType<typeof applyFiBasisToFifoResult>['details']
): number {
  let n = 0;
  for (const d of details) {
    const fifoG = d.fifoGainEur ?? d.gainEur;
    const chosen = d.chosenGainEur ?? d.gainEur;
    if (
      d.basisMethodUsed === 'deemed' &&
      fifoG != null &&
      fifoG.lt(chosen) &&
      (d.fifoCostBasisEur ?? d.costEur).lte(ZERO_EPS)
    ) {
      n++;
    }
  }
  return n;
}

export function buildLotBasisRepairDiagnosticsCsv(stats: LotBasisRepairStats): string {
  const rows: string[][] = [
    ['zero_basis_candidates_pass1', String(stats.zeroBasisCandidatesPass1)],
    ['repaired_total', String(stats.repairedTotal)],
    ['unresolved_candidates', String(stats.unresolvedCandidates)],
    [
      'disposals_fifo_cost_zero_pass1',
      String(stats.disposalsWithFifoCostZeroPass1),
    ],
    [
      'disposals_fifo_cost_zero_pass2',
      String(stats.disposalsWithFifoCostZeroPass2),
    ],
    ['delta_fifo_gain_eur', stats.deltaFifoGainEur.toFixed(8)],
    ['delta_chosen_gain_eur', stats.deltaChosenGainEur.toFixed(8)],
    [
      'deemed_only_because_fifo_zero_pass1_estimate',
      String(stats.deemedOnlyBecauseFifoZeroPass1Estimate),
    ],
    ['disposal_outgoing_fallback_count', String(stats.disposalOutgoingFallbackCount)],
    [
      'synthetic_stable_inventory_backfills',
      String(stats.syntheticStableInventoryBackfills),
    ],
    ['mission_reward_row_count', String(stats.missionRewardRowCount)],
    ['deemed_to_fifo_switch_count', String(stats.deemedToFifoSwitchCount)],
  ];
  if (stats.walletContinuity) {
    const w = stats.walletContinuity;
    rows.push(
      ['wc_owned_to_owned_internal_rows', String(w.ownedToOwnedInternalRows)],
      ['wc_lot_moves_succeeded', String(w.lotMovesSucceeded)],
      ['wc_lot_moves_skipped', String(w.lotMovesSkipped)],
      ['wc_same_tx_transfer_in_suppressed', String(w.sameTxSpuriousTransferInSuppressed)],
      ['wc_disposal_omnibus_fallback_legs', String(w.disposalOmnibusFallbackLegs)]
    );
  }
  if (stats.continuityChosenGainDeltaVsGlobalEur != null) {
    rows.push([
      'continuity_chosen_gain_delta_vs_global_eur',
      stats.continuityChosenGainDeltaVsGlobalEur.toFixed(8),
    ]);
  }
  if (stats.disposalsUnknownAcquisitionProvenancePass2 != null) {
    rows.push([
      'disposals_unknown_acquisition_provenance_pass2',
      String(stats.disposalsUnknownAcquisitionProvenancePass2),
    ]);
  }
  if (stats.crossEnvironment) {
    const x = stats.crossEnvironment;
    rows.push(
      ['ce_cdc_omnibus_to_chain_matches', String(x.cdcOmnibusToChainMatches)],
      ['ce_chain_transfer_in_suppressed', String(x.chainTransferInSuppressed)],
      ['ce_synthetic_omnibus_to_wallet', String(x.syntheticOmnibusToWalletInserted)],
      ['ce_unmatched_chain_transfer_in', String(x.unmatchedChainTransferInCandidates)],
      ['ce_unmatched_cdc_internal', String(x.unmatchedCdcInternalCandidates)]
    );
  }
  if (stats.cronosBridge) {
    const b = stats.cronosBridge;
    rows.push(
      ['bridge_pairs_matched', String(b.pairsMatched)],
      ['bridge_disposal_legs_removed', String(b.disposalLegsRemoved)],
      ['bridge_acquisition_legs_removed', String(b.acquisitionLegsRemoved)],
      ['bridge_synthetic_internal_inserted', String(b.syntheticInternalInserted)],
      ['bridge_same_wallet_pairs_suppressed', String(b.sameWalletPairsSuppressed)],
      ['bridge_cro_amount_bridged', b.croAmountBridged.toFixed(8)],
      ['bridge_unpaired_disposal_to_bridge', String(b.unpairedDisposalToBridge)],
      ['bridge_unpaired_acquisition_from_bridge', String(b.unpairedAcquisitionFromBridge)],
      ['bridge_review_flags_added', String(b.reviewFlagsAdded)]
    );
  }
  if (stats.bridgeContinuityChosenGainDeltaPass1Eur != null) {
    rows.push([
      'bridge_continuity_chosen_gain_delta_pass1_eur',
      stats.bridgeContinuityChosenGainDeltaPass1Eur.toFixed(8),
    ]);
  }
  for (const [k, v] of Object.entries(stats.repairedByAsset).sort((a, b) =>
    a[0].localeCompare(b[0])
  )) {
    rows.push([`repaired_asset_${k}`, String(v)]);
  }
  for (const [k, v] of Object.entries(stats.repairedByMethod).sort((a, b) =>
    a[0].localeCompare(b[0])
  )) {
    rows.push([`repaired_method_${k}`, String(v)]);
  }
  for (const [k, v] of Object.entries(stats.unresolvedByAsset).sort((a, b) =>
    a[0].localeCompare(b[0])
  )) {
    rows.push([`unresolved_asset_${k}`, String(v)]);
  }
  for (const [k, v] of Object.entries(stats.unresolvedByClassification).sort((a, b) =>
    a[0].localeCompare(b[0])
  )) {
    rows.push([`unresolved_class_${k}`, String(v)]);
  }
  return buildMetricValueCsv(rows);
}

export type LotBasisRepairPipelineResult = {
  pass1: FifoResult;
  pass1WithFi: ReturnType<typeof applyFiBasisToFifoResult>;
  pass2: FifoResult;
  pass2WithFi: ReturnType<typeof applyFiBasisToFifoResult>;
  stats: LotBasisRepairStats;
  repairedEvents: LedgerEvent[];
};

/**
 * Pass 1: FIFO on input events. Repair stage: fill zero-basis acquisition/income EUR. Pass 2: FIFO on repaired ledger.
 * When `ownedWallets` is set: same-tx duplicate transfer-in suppression + wallet-scoped FIFO with lot moves on internal transfers.
 */
export async function runLotBasisRepairPipeline(
  events: LedgerEvent[],
  options?: { signal?: AbortSignal; ownedWallets?: OwnedWallet[] }
): Promise<LotBasisRepairPipelineResult> {
  const signal = options?.signal;
  const ownedSet = options?.ownedWallets?.length
    ? ownedAddressSetFromWallets(options.ownedWallets)
    : new Set<string>();

  let pipelineEvents = cloneLedgerEvents(events);
  let crossEnvironment: CrossEnvironmentContinuityStats | undefined;
  if (ownedSet.size > 0) {
    const ce = applyCrossEnvironmentContinuity(pipelineEvents, {
      ownedAddresses: ownedSet,
    });
    pipelineEvents = ce.events;
    crossEnvironment = ce.stats;
  }

  const eventsAfterCrossEnv = cloneLedgerEvents(pipelineEvents);

  const bridgeContractSet = mergedCronosBridgeContracts();
  let cronosBridge: CronosBridgeContinuityStats | undefined;
  if (ownedSet.size > 0 && bridgeContractSet.size > 0) {
    const br = applyCronosBridgeMigrationContinuity(pipelineEvents, {
      ownedAddresses: ownedSet,
      bridgeContracts: bridgeContractSet,
    });
    pipelineEvents = br.events;
    cronosBridge = br.stats;
  }

  const ledgerAfterBridgeBeforeWalletPrep = cloneLedgerEvents(pipelineEvents);

  let sameTxSuppressed = 0;
  if (options?.ownedWallets?.length) {
    const prep = applyWalletContinuityLedgerPrep(pipelineEvents, options.ownedWallets);
    pipelineEvents = prep.events;
    sameTxSuppressed = prep.sameTxSpuriousTransferInSuppressed;
  }

  let syntheticStableInventoryBackfills = 0;
  if (ownedSet.size === 0) {
    const inj = await injectSyntheticStablecoinAcquisitionsForShortfall(pipelineEvents, { signal });
    pipelineEvents = inj.events;
    syntheticStableInventoryBackfills = inj.inserted;
  }

  function runFifoPass(ev: LedgerEvent[]): FifoResult {
    if (ownedSet.size > 0) {
      return fifoResultWithoutWalletContinuity(
        runFifoWithOwnedWalletContinuity(ev, { ownedAddresses: ownedSet })
      );
    }
    return runFifo(ev);
  }

  const ledgerPreparedForPass1Baseline = async (
    base: LedgerEvent[]
  ): Promise<LedgerEvent[]> => {
    let e = cloneLedgerEvents(base);
    if (options?.ownedWallets?.length) {
      e = applyWalletContinuityLedgerPrep(e, options.ownedWallets).events;
    }
    if (ownedSet.size === 0) {
      const inj = await injectSyntheticStablecoinAcquisitionsForShortfall(e, { signal });
      e = inj.events;
    }
    return e;
  };

  let bridgeContinuityChosenGainDeltaPass1Eur: Decimal | undefined;
  if (ownedSet.size > 0 && cronosBridge != null && bridgeContractSet.size > 0) {
    const preparedNoBridge = await ledgerPreparedForPass1Baseline(eventsAfterCrossEnv);
    const preparedWithBridge = await ledgerPreparedForPass1Baseline(
      ledgerAfterBridgeBeforeWalletPrep
    );
    const pass1NoBridgeFi = applyFiBasisToFifoResult(runFifoPass(preparedNoBridge));
    const pass1WithBridgeFi = applyFiBasisToFifoResult(runFifoPass(preparedWithBridge));
    bridgeContinuityChosenGainDeltaPass1Eur = sumChosenGains(pass1WithBridgeFi).minus(
      sumChosenGains(pass1NoBridgeFi)
    );
  }

  const pass1 = runFifoPass(pipelineEvents);
  const pass1WithFi = applyFiBasisToFifoResult(pass1);

  const disposalOutgoingFallbackCount = countOutgoingFallbackDisposals(events);
  const missionRewardRowCount = countMissionRewardRows(events);

  const repaired = cloneLedgerEvents(pipelineEvents);
  initAcquisitionProvenance(repaired);

  const zeroBasisCandidatesPass1 = repaired.filter(isLotBasisRepairCandidate).length;

  for (const ev of repaired) {
    if (!isLotBasisRepairCandidate(ev)) continue;
    tryRepairExplicitNativeEur(ev);
  }

  repairSiblingSameRefWeightedSameAsset(repaired);
  await repairAsyncFallbacks(repaired, signal);

  const repairedByAsset: Record<string, number> = {};
  const repairedByMethod: Record<string, number> = {};
  let repairedTotal = 0;
  for (const ev of repaired) {
    if (!ev.lotBasisRepair) continue;
    repairedTotal++;
    const a = ev.asset.toUpperCase();
    const m = ev.lotBasisRepair.basisRepairMethod;
    repairedByAsset[a] = (repairedByAsset[a] ?? 0) + 1;
    repairedByMethod[m] = (repairedByMethod[m] ?? 0) + 1;
  }

  const unresolvedCandidates = repaired.filter(isLotBasisRepairCandidate).length;

  const unresolvedByAsset: Record<string, number> = {};
  const unresolvedByClassification: Record<string, number> = {};
  for (const ev of repaired.filter(isLotBasisRepairCandidate)) {
    const a = ev.asset.toUpperCase();
    unresolvedByAsset[a] = (unresolvedByAsset[a] ?? 0) + 1;
    const c = ev.classification ?? 'unset';
    unresolvedByClassification[c] = (unresolvedByClassification[c] ?? 0) + 1;
    mergeFlags(ev, ['lot_basis_unresolved']);
  }

  const pass2Raw =
    ownedSet.size > 0
      ? runFifoWithOwnedWalletContinuity(repaired, { ownedAddresses: ownedSet })
      : null;
  const pass2 = pass2Raw
    ? fifoResultWithoutWalletContinuity(pass2Raw)
    : runFifo(repaired);
  const pass2WithFi = applyFiBasisToFifoResult(pass2);

  let continuityChosenGainDeltaVsGlobalEur: Decimal | undefined;
  if (ownedSet.size > 0) {
    const globalP2Fi = applyFiBasisToFifoResult(runFifo(repaired));
    continuityChosenGainDeltaVsGlobalEur = sumChosenGains(pass2WithFi).minus(
      sumChosenGains(globalP2Fi)
    );
  }

  const disposalsUnknownAcquisitionProvenancePass2 = pass2WithFi.details.filter(
    (d) => d.acquisitionProvenance === 'unknown'
  ).length;

  const walletContinuity: WalletContinuityFifoStats | undefined = pass2Raw
    ? { ...pass2Raw.walletContinuity, sameTxSpuriousTransferInSuppressed: sameTxSuppressed }
    : sameTxSuppressed > 0
      ? {
          ownedToOwnedInternalRows: 0,
          lotMovesSucceeded: 0,
          lotMovesSkipped: 0,
          sameTxSpuriousTransferInSuppressed: sameTxSuppressed,
          disposalOmnibusFallbackLegs: 0,
        }
      : undefined;

  const deemedToFifoSwitchCount = countDeemedToFifoSwitches(
    pass1WithFi.details,
    pass2WithFi.details
  );

  const disposalsWithFifoCostZeroPass1 = countFifoZeroCostDisposals(pass1WithFi);
  const disposalsWithFifoCostZeroPass2 = countFifoZeroCostDisposals(pass2WithFi);

  const deltaFifoGainEur = sumFifoGains(pass2WithFi).minus(sumFifoGains(pass1WithFi));
  const deltaChosenGainEur = sumChosenGains(pass2WithFi).minus(
    sumChosenGains(pass1WithFi)
  );

  const deemedOnlyBecauseFifoZeroPass1Estimate = countDeemedWhileFifoLoss(
    pass1WithFi.details
  );

  const stats: LotBasisRepairStats = {
    zeroBasisCandidatesPass1,
    repairedTotal,
    unresolvedCandidates,
    repairedByAsset,
    repairedByMethod,
    unresolvedByAsset,
    unresolvedByClassification,
    disposalsWithFifoCostZeroPass1,
    disposalsWithFifoCostZeroPass2,
    deltaFifoGainEur,
    deltaChosenGainEur,
    deemedOnlyBecauseFifoZeroPass1Estimate,
    disposalOutgoingFallbackCount,
    syntheticStableInventoryBackfills,
    missionRewardRowCount,
    deemedToFifoSwitchCount,
    walletContinuity,
    continuityChosenGainDeltaVsGlobalEur,
    disposalsUnknownAcquisitionProvenancePass2,
    crossEnvironment,
    cronosBridge,
    bridgeContinuityChosenGainDeltaPass1Eur,
  };

  return {
    pass1,
    pass1WithFi,
    pass2,
    pass2WithFi,
    stats,
    repairedEvents: repaired,
  };
}
